import { FC, memo, Suspense } from 'react';
import { cloneDeep } from 'lodash';
import type { Node } from 'reactflow';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { Modal, Button, BusyLoader } from '@geneui/components';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
// Import Components
import { EditForm } from './forms';
// Import Constants
import { l, CustomerJourneyGroupBlockTypesLabels } from 'constants/common';

interface Props {
    isLoading: boolean;
    isOkActive?: boolean;
    isVisibleModal: boolean;
    block: Nullable<Node>;
    elements: Node[];
    onOk: (data: Node) => void;
    onCancel: () => void;
}

const EditModal: FC<Props> = ({ isVisibleModal, isOkActive = true, isLoading, block, elements, onOk, onCancel }) => {
    const { t } = useTranslation();

    const blockGroupTypeLabel = CustomerJourneyGroupBlockTypesLabels[block?.type ?? ''];
    const blockTypeLabel = `CJBlock${block?.data?.blockType}`;

    const clonedBlock = cloneDeep(block) ?? {};

    const onCancelHandler = () => {
        onCancel();
    };

    const onOkHandler = () => {
        onOk({ ...clonedBlock } as Node);
    };

    return (
        <Modal
            background="dark-background"
            position="center"
            size="content-size"
            className="crm-modal crm-cj-edit-modal"
            title={`${t(blockGroupTypeLabel)} ${t(blockTypeLabel)}`}
            closable={false}
            closeOnClickOutside={true}
            onCancel={onCancel}
            visible={isVisibleModal}
            isOkActive={isOkActive}
            footer={
                <>
                    <Button color="default" appearance="minimal" onClick={onCancelHandler}>
                        {t(l.Cancel)}
                    </Button>
                    <ButtonWithLoader
                        color="primary"
                        onClick={onOkHandler}
                        isLoading={isLoading}
                        isDisabled={!isOkActive}
                    >
                        {t(l.Save)}
                    </ButtonWithLoader>
                </>
            }
        >
            <Suspense fallback={<BusyLoader isBusy type="spinner" spinnerSize="big" />}>
                <EditForm block={clonedBlock} elements={elements} blockTypeLabel={blockTypeLabel} />
            </Suspense>
        </Modal>
    );
};

export default memo(EditModal);
